import { FhirInstance } from '../config/FhirClient';
import { IBundle } from '../modules/unsw-fhir/Bundle';
import {
  IOperationOutcome,
  isOperationOutcome,
} from '../modules/unsw-fhir/OperationOutcome';
import {
  isPractitioner,
  IPractitioner,
} from '../modules/unsw-fhir/Practitioner';
import { getNextLinkStr, getSelfLinkStr } from '../selectors/bundle';

type ResponsePayload = {
  next?: string;
  data: IPractitioner[];
  previous?: string;
};

export const getPractitioners = async (
  count?: string,
  link?: string,
  sorted?: { id: string; value: string }[],
  summary: boolean = true,
  active: boolean = true,
): Promise<ResponsePayload> => {
  const path = link ? link : '/Practitioner';
  const params: {
    [key: string]: string | number | boolean;
  } = {};
  if (!link && count) params._count = count;
  if (!summary) params._summary = summary;
  if (!!sorted)
    for (const value of sorted) {
      params[value.id] = value.value;
    }
  if (active) params.active = active;
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(path, {
      params,
    });
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return { data: [] };
    }

    const retV: ResponsePayload = {
      previous: getSelfLinkStr(data),
      data: [],
      next: getNextLinkStr(data),
    };

    retV.data = data.entry
      .filter(entry => {
        return !!entry.resource && isPractitioner(entry.resource);
      })
      .map((entry): IPractitioner => entry.resource as IPractitioner);

    return retV;
  } catch (e) {
    console.error(e);
    return { data: [] };
  }
};

export const postPractitioner = async (practitioner: IPractitioner) => {
  try {
    const response = await FhirInstance.post<IPractitioner | IOperationOutcome>(
      '/Practitioner',
      practitioner,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const putPractitioner = async (
  id: string,
  practitioner: IPractitioner,
) => {
  try {
    const response = await FhirInstance.put<IPractitioner | IOperationOutcome>(
      `/Practitioner/${id}`,
      practitioner,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getPractitionerById = async (id: string) => {
  try {
    const response = await FhirInstance.get<IPractitioner | IOperationOutcome>(
      `/Practitioner/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getPractitionerByEmail = async (email: string) => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/Practitioner',
      { params: { email } },
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return undefined;
    }
    return data.entry
      .filter(entry => {
        return !!entry.resource && isPractitioner(entry.resource);
      })
      .map((entry): IPractitioner => entry.resource as IPractitioner)[0];
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const archivePractitioner = async (id: string) => {
  try {
    const response = await FhirInstance.patch<
      IPractitioner | IOperationOutcome
    >(`/Practitioner/${id}`, { active: false });
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
export const deletePractitioner = async (id: string) => {
  try {
    const response = await FhirInstance.delete<
      IPractitioner | IOperationOutcome
    >(`/Practitioner/${id}`);
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
